import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import PipeIcon from "../../assets/core/pipe-icon.svg?react";
import { useGetUserCreatedPlaylistsQuery } from "../../domains/user/hooks/useGetUserCreatedPlaylistsQuery";
import { apiClient, useApiRevalidate } from "../../services/api";
import { ClientError } from "../../../../api/src/shared/request";
import AppCheckbox from "../AppCheckbox";
import { Input } from "../ui/Input";
import ArticlePublicationCard from "../cards/ArticlePublicationCard";
import VideoCard from "../cards/VideoCard";

import InfographicCard from "../cards/InfographicCard";
import HeadphoneIcon from "../../assets/icons/headphone-fill.svg?react";
import {
  CardColumn,
  CardContainer,
  CardRow,
  CardTitle,
  CardType,
} from "../cards/layout";
import { useAudioContext } from "../../domains/app/contexts/audio.context";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { Button } from "../ui/Button";
import { displayToast } from "./AppToast";
import BottomPanel from "./panels/BottomPanel";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { gaPushEvent } from "../../tools/analytics/base.analytics";
import type { PlaylistPanelContentItem } from "../../domains/playlist/hooks/usePlaylistPanelContent";

type Mode = "empty" | "list" | "create";

export default function PlaylistPanel({
  isVisible,
  contentToAdd,
  onClose,
  onSave,
}: {
  isVisible: boolean;
  contentToAdd: PlaylistPanelContentItem;
  onClose: () => void;
  onSave: (params: { add: string[]; remove: string[] }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const revalidate = useApiRevalidate();
  const { playlists: createdPlaylists } = useGetUserCreatedPlaylistsQuery();
  const [isCreatingPlaylist, setCreatingPlaylist] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [startingPlaylistsIds, setStartingPlaylistsIds] = useState<string[]>(
    []
  );
  const [selectedPlaylistsIds, setSelectedPlaylistsIds] = useState<string[]>(
    []
  );
  /**
   * Tracks what content+playlist state was used for the last initialization.
   * Re-initializes when content changes or when playlists load after panel opens.
   */
  const initStateRef = useRef<{
    contentId: string;
    hadData: boolean;
  } | null>(null);

  const playlistsWithContent = useMemo(() => {
    if (!isVisible) {
      return [];
    }
    return createdPlaylists.filter((playlist) => {
      const allIds = [...playlist.playlist, ...playlist.playlistContent];
      return allIds.includes(contentToAdd.id);
    });
  }, [isVisible, createdPlaylists, contentToAdd.id]);

  const playlistsWithContentIds = useMemo(
    () => playlistsWithContent.map(({ id }: { id: string }) => id),
    [playlistsWithContent]
  );

  useEffect(() => {
    if (!isVisible) {
      initStateRef.current = null;
      setCreatingPlaylist(false);
      setNewPlaylistName("");
      return;
    }

    const contentId = contentToAdd.id;
    const init = initStateRef.current;
    const isNewContent = init === null || init.contentId !== contentId;
    const wasEmptyNowHasData =
      init !== null && !init.hadData && playlistsWithContentIds.length > 0;

    if (isNewContent || wasEmptyNowHasData) {
      initStateRef.current = {
        contentId,
        hadData: playlistsWithContentIds.length > 0,
      };
      setStartingPlaylistsIds([...playlistsWithContentIds]);
      setSelectedPlaylistsIds([...playlistsWithContentIds]);
    }
  }, [isVisible, contentToAdd.id, playlistsWithContentIds]);

  function handleCheckPlaylist(playlistId: string) {
    if (selectedPlaylistsIds.includes(playlistId)) {
      setSelectedPlaylistsIds(
        selectedPlaylistsIds.filter((id) => id !== playlistId)
      );
    } else {
      setSelectedPlaylistsIds([...selectedPlaylistsIds, playlistId]);
    }
  }

  async function handleCreatePlaylist() {
    try {
      if (newPlaylistName.length === 0) return;

      setSaving(true);
      const response = await apiClient.post("/webapp/private/playlists", {
        json: { title: newPlaylistName },
      });
      gaPushEvent("playlist_created", {
        origin: "playlist_panel",
        title: newPlaylistName,
      });

      await revalidate("/webapp/private/playlists/me");
      displayToast(t("Collection created. Added to your profile."), "success");

      await onSave({ add: [response.body.id], remove: [] });
      setSaving(false);
      onClose();
    } catch (error) {
      if (error instanceof ClientError && error.responseStatus === 409) {
        displayToast(
          t(
            "🥺 This playlist title already exists. Give it a unique sweet name."
          ),
          "error"
        );
      } else {
        displayToast(t("Something went wrong. Please try again."), "error");
      }
      setSaving(false);
    }
  }

  async function handleSaveChanges() {
    const add = createdPlaylists
      .filter((playlist) => {
        return (
          selectedPlaylistsIds.includes(playlist.id) &&
          !startingPlaylistsIds.includes(playlist.id)
        );
      })
      .map((playlist) => playlist.id);

    const remove = createdPlaylists
      .filter((playlist) => {
        return (
          !selectedPlaylistsIds.includes(playlist.id) &&
          startingPlaylistsIds.includes(playlist.id)
        );
      })
      .map((playlist) => playlist.id);

    if (add.length > 0 || remove.length > 0) {
      try {
        setSaving(true);
        await onSave({ add, remove });
      } finally {
        setSaving(false);
      }
    }
    onClose();
  }

  const mode: Mode = isCreatingPlaylist
    ? "create"
    : createdPlaylists.length === 0
      ? "empty"
      : "list";

  const title =
    mode === "create"
      ? t("Create new playlist")
      : mode === "empty"
        ? t("Create a playlist")
        : t("Add to playlist");

  const actions =
    mode === "create" ? (
      <Button
        variant="primary"
        size="lg"
        className="w-full"
        disabled={newPlaylistName.length === 0 || isSaving}
        onClick={handleCreatePlaylist}
      >
        {t("Save")}
      </Button>
    ) : mode === "list" ? (
      <>
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => {
            setCreatingPlaylist(true);
          }}
        >
          {t("Create new playlist")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isSaving}
          onClick={handleSaveChanges}
        >
          {t("Save")}
        </Button>
      </>
    ) : null;

  return (
    <BottomPanel
      isOpen={isVisible}
      onClose={() => {
        onClose();
      }}
      title={title}
      displayCloseButton
      actions={actions}
    >
      {mode === "empty" && (
        <div
          className="pt-[20px] px-[32px] flex flex-col items-center gap-[20px]"
          style={{
            paddingBottom: "calc(32px + var(--safe-area-bottom))",
          }}
        >
          <PipeIcon className="size-[100px] " />
          <p className="mx-[32px] my-0 text-[15px] text-center">
            <Trans
              i18nKey="<b>Hi {{name}}</b>, it seems that you haven't created any playlist yet.<br><br>Create one right now to consult papers by topics or specialties any time you want."
              values={{
                name: currentUser?.firstname ?? t("Doc"),
              }}
              components={{ b: <b /> }}
            />
          </p>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => {
              setCreatingPlaylist(true);
            }}
          >
            {t("Create a playlist")}
          </Button>
        </div>
      )}

      {mode === "list" && (
        <div
          className="p-[20px] flex flex-col"
          style={{
            paddingBottom: "calc(150px + var(--safe-area-bottom))",
          }}
        >
          <ContentCard content={contentToAdd} />
          <p className="mt-[35px] mb-[20px]">
            {t("Add this publication to playlist(s)")}
          </p>
          {createdPlaylists.map((playlist) => {
            return (
              <AppCheckbox
                key={playlist.id}
                checked={selectedPlaylistsIds.includes(playlist.id)}
                onChange={() => {
                  handleCheckPlaylist(playlist.id);
                }}
              >
                <span className="text-[18px] font-semibold text-primary-black">
                  {playlist.title}
                </span>
              </AppCheckbox>
            );
          })}
        </div>
      )}

      {mode === "create" && (
        <div className="overflow-hidden pt-[30px] pb-[150px] px-5">
          <Input
            value={newPlaylistName}
            onInput={(e: ChangeEvent<HTMLInputElement>) => {
              setNewPlaylistName(e.target.value);
            }}
            placeholder={t("Playlist name")}
          />
        </div>
      )}
    </BottomPanel>
  );
}

function ContentCard({ content }: { content: PlaylistPanelContentItem }) {
  switch (content.content_format) {
    case "Article": {
      return (
        <ArticlePublicationCard
          roomId={null}
          article={content}
          analyticsListName={AnalyticsItemList.PLAYLIST_PANEL}
          recommendation={null}
          onClick={null}
          className={null}
          showImage={false}
        />
      );
    }
    case "Video": {
      return (
        <VideoCard
          roomId={null}
          video={content}
          analyticsListName={AnalyticsItemList.PLAYLIST_PANEL}
          recommendation={null}
          onClick={null}
          className={null}
        />
      );
    }
    case "Infographic": {
      return (
        <InfographicCard
          roomId={null}
          infographic={content}
          analyticsListName={AnalyticsItemList.PLAYLIST_PANEL}
          recommendation={null}
          onClick={null}
          className={null}
        />
      );
    }
    case "Audio": {
      return <PlaylistAudioCard content={content} />;
    }
    default: {
      throw new Error(content satisfies never);
    }
  }
}

type PlaylistAudioContent = Extract<
  PlaylistPanelContentItem,
  { content_format: "Audio" }
>;

function formatDuration(seconds: number): string {
  if (seconds === 0) return "";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function PlaylistAudioCard({ content }: { content: PlaylistAudioContent }) {
  const { t } = useTranslation();
  const {
    setTracks,
    setTrackTitle,
    setTrackImageUrl,
    setTrackCollectionName,
    setAudioPlayerPanelOpen,
  } = useAudioContext();

  const audioUrl = content.audio === null ? null : content.audio.url;
  const thumbnailUrl =
    content.thumbnail === null ? null : content.thumbnail.url;
  const duration = content.duration === null ? 0 : content.duration;

  function handleClick() {
    if (audioUrl === null || thumbnailUrl === null) return;
    setTracks({
      track: { kind: "file" },
      contentList: [
        {
          type: "playlist_audio_content",
          source: content,
          title: content.title,
          trackUrl: audioUrl,
          imageUrl: thumbnailUrl,
        },
      ],
      index: 0,
      autoplay: true,
    });
    setTrackTitle(content.title);
    setTrackImageUrl(thumbnailUrl);
    setTrackCollectionName("");
    setAudioPlayerPanelOpen(true);
  }

  return (
    <CardContainer
      variant="dark"
      backgroundUrl={thumbnailUrl}
      style={{ minHeight: "192px" }}
      onClick={handleClick}
      backgroundImage={null}
    >
      <CardRow $gap={16}>
        <CardColumn>
          <CardType>{t("Audio")}</CardType>
          <CardTitle lines={4}>{content.title}</CardTitle>
        </CardColumn>

        {audioUrl !== null && (
          <CardColumn className="items-center">
            <div className="size-8 rounded-full bg-white flex items-center justify-center">
              <HeadphoneIcon />
            </div>
            <span className="text-white text-[10px]">
              {formatDuration(duration)}
            </span>
          </CardColumn>
        )}
      </CardRow>
    </CardContainer>
  );
}
